import {
	Box,
	FormLabel,
	Grid,
	HStack,
	Tab,
	TabList,
	TabPanel,
	TabPanels,
	Tabs,
	Text,
} from "@chakra-ui/react";
import { __ } from "@wordpress/i18n";
import React from "react";
import AdvancedControls from "../../../components/common/AdvancedControls";
import InspectorControlTabs, {
	AdvancedInspectorControls,
	GeneralInspectorControls,
	StyleInspectorControls,
} from "../../../components/common/InspectorTabs";
import Panel from "../../../components/common/Panel";
import ResponsiveControls from "../../../components/common/ResponsiveControls";
import ZStack from "../../../components/common/ZStack";
import {
	AlignBottom,
	AlignCenter,
	AlignLeft,
	AlignMiddle,
	AlignRight,
	AlignTop,
	Global,
	Icon1,
	Icon2,
	Icon3,
	Icon4,
	Icon5,
	Icon6,
} from "../../../components/icons/Icons";
import {
	Background,
	Border,
	BoxShadow,
	ColorPicker,
	Dimensions,
	IconPicker,
	PopoverDrawer,
	Slider,
	ToggleButton,
	ToggleButtonGroup,
	UrlInput,
} from "../../../controls";
import DevicePicker from "../../../controls/common/DevicePicker";
import Reset from "../../../controls/common/Reset";
import useDevice from "../../../hooks/useDevice";
import { isCSSVariable } from "../../../utils";

type Props = {
	attributes: any;
	setAttributes: (attributes: any) => void;
};

const InspectorControls: React.ComponentType<Props> = (props) => {
	const {
		attributes: {
			border,
			hoverBorder,
			boxShadow,
			boxShadowHover,
			blockPadding,
			blockZIndex,
			cssID,
			hideOnDesktop,
			hideOnTablet,
			hideOnMobile,
			className,
			presets,
			icon,
			iconSize,
			iconOpacity,
			iconRotation,
			color,
			background,
			alignment,
			shapeColor,
			shapeBorderRadius,
			shapePadding,
			shapeOutlineWidth,
			shapeOutlineColor,
			minHeight,
			contentVerticalAlign,
			maxContentWidth,
			contentHorizontalAlign,
			link,
			blockMargin,
		},
		setAttributes,
	} = props;

	const { device, setDevice } = useDevice();

	return (
		<>
			<InspectorControlTabs />
			<GeneralInspectorControls>
				<Panel
					title={__("Layout", "magazine-blocks")}
					initialOpen={true}
				>
					<ToggleButtonGroup
						value={contentHorizontalAlign}
						onChange={(v) => {
							setAttributes({ contentHorizontalAlign: v });
						}}
						label={__("Horizontal Alignment", "magazine-blocks")}
						groupProps={{
							sx: {
								".chakra-button": {
									minH: "32px",
									svg: {
										w: 6,
										h: 6,
									},
									"&[data-active]": {
										svg: {
											fill: "#fff !important",
										},
									},
								},
							},
						}}
					>
						<ToggleButton value="left">
							<AlignLeft />
						</ToggleButton>
						<ToggleButton value="center">
							<AlignCenter />
						</ToggleButton>
						<ToggleButton value="right">
							<AlignRight />
						</ToggleButton>
					</ToggleButtonGroup>
					<ToggleButtonGroup
						value={contentVerticalAlign}
						onChange={(v) => {
							setAttributes({ contentVerticalAlign: v });
						}}
						label={__("Vertical Alignment", "magazine-blocks")}
						groupProps={{
							sx: {
								".chakra-button": {
									minH: "32px",
									svg: {
										w: 6,
										h: 6,
									},
									"&[data-active]": {
										svg: {
											fill: "#fff !important",
										},
									},
								},
							},
						}}
					>
						<ToggleButton value="start">
							<AlignTop />
						</ToggleButton>
						<ToggleButton value="center">
							<AlignMiddle />
						</ToggleButton>
						<ToggleButton value="end">
							<AlignBottom />
						</ToggleButton>
					</ToggleButtonGroup>
					<Slider
						label={__("Min Height", "magazine-blocks")}
						value={minHeight}
						min={0}
						max={1000}
						step={1}
						onChange={(val) => setAttributes({ minHeight: val })}
						resetAttributeKey="minHeight"
					/>
				</Panel>
				<Panel title={__("Icon", "magazine-blocks")}>
					<HStack align="center" justify="space-between" mb="2">
						<HStack align="center">
							<FormLabel>
								{__("Presets", "magazine-blocks")}
							</FormLabel>
							<DevicePicker
								device={device}
								setDevice={setDevice}
							/>
						</HStack>
					</HStack>
					<Grid
						templateColumns={{
							base: "repeat(1, 1fr)",
							md: "repeat(2, 1fr)",
						}}
						gap={3}
					>
						{[
							{
								label: __("Fill", "magazine-blocks"),
								value: "fill-preset",
								icon: Icon1,
							},
							{
								label: __("Outline", "magazine-blocks"),
								value: "outline-preset",
								icon: Icon2,
							},
							{
								label: __("Rect. Fill", "magazine-blocks"),
								value: "rectangular-fill-preset",
								icon: Icon3,
							},
							{
								label: __("Rect. Outline", "magazine-blocks"),
								value: "rectangular-outline-preset",
								icon: Icon4,
							},
							{
								label: __("Rounded Fill", "magazine-blocks"),
								value: "rounded-fill-preset",
								icon: Icon5,
							},
							{
								label: __("Rounded Outline", "magazine-blocks"),
								value: "rounded-outline-preset",
								icon: Icon6,
							},
						].map((item, idx) => {
							const isSelected = presets === item.value;

							return (
								<Box
									key={item.value}
									bg="gray.50"
									role="button"
									tabIndex={0}
									onClick={() =>
										setAttributes({
											presets: item.value,
										})
									}
									borderRadius="base"
									border={
										isSelected ? "2px solid" : "1px solid"
									}
									borderColor={
										isSelected ? "purple.500" : "gray.200"
									}
									height="70px"
									display="flex"
									flexDirection="column"
									alignItems="center"
									justifyContent="center"
									cursor="pointer"
									p={1}
								>
									<Box
										as={item.icon}
										width="full"
										height="full"
									/>
									<Text
										fontSize="xs"
										fontWeight="semibold"
										mb={0}
										pt={0.5}
									>
										{item.label ||
											__("Style", "magazine-blocks")}{" "}
										{!item.label && idx + 1}
									</Text>
								</Box>
							);
						})}
					</Grid>
					<IconPicker
						value={icon}
						onChange={(val) => setAttributes({ icon: val })}
					/>
					<PopoverDrawer
						label={__("Icon Color", "magazine-blocks")}
						trigger={(props) => {
							const Trigger = () => (
								<PopoverDrawer.Trigger {...props}>
									<ZStack
										items={[{ color: color, id: "color" }]}
									/>
								</PopoverDrawer.Trigger>
							);
							if (isCSSVariable(color)) {
								return (
									<Box position="relative">
										<Trigger />
										<Global
											position="absolute"
											fill="primary.500"
											height="14px"
											width="14px"
											top="-5px"
											left="-5px"
											bg="white"
											borderRadius="full"
										/>
									</Box>
								);
							}
							return <Trigger />;
						}}
						closeOnFocusOutside
					>
						<Tabs position="relative">
							<TabList>
								<Tab>{__("Normal", "magazine-blocks")}</Tab>
							</TabList>
							<TabPanels>
								<TabPanel>
									<Reset
										saved={color}
										resetKey="color"
										onReset={(v) => {
											setAttributes({ color: v });
										}}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									/>
									<ColorPicker
										value={color}
										onChange={(v) =>
											setAttributes({ color: v })
										}
									/>
								</TabPanel>
							</TabPanels>
						</Tabs>
					</PopoverDrawer>
					<Slider
						label={__("Icon Size", "magazine-blocks")}
						value={iconSize}
						min={0}
						max={100}
						step={1}
						onChange={(val) => setAttributes({ iconSize: val })}
						resetAttributeKey="iconSize"
					/>
					<Slider
						label={__("Icon Opacity", "magazine-blocks")}
						value={iconOpacity}
						min={0}
						max={1}
						step={0.1}
						onChange={(val) => setAttributes({ iconOpacity: val })}
						resetAttributeKey="iconOpacity"
					/>
					<Slider
						label={__("Icon Rotation", "magazine-blocks")}
						value={iconRotation}
						min={0}
						max={360}
						step={1}
						onChange={(val) => setAttributes({ iconRotation: val })}
						resetAttributeKey="iconRotation"
					/>
					<UrlInput
						label={__("URL", "magazine-blocks")}
						onChange={(val) => setAttributes({ link: val })}
						value={link}
						placeholder="https://"
						newTab
					/>
				</Panel>
			</GeneralInspectorControls>
			<StyleInspectorControls>
				<Panel title={__("Container", "magazine-blocks")}>
					<PopoverDrawer label={__("Background", "magazine-blocks")}>
						<Tabs position="relative">
							<TabList>
								<Tab>{__("Normal", "magazine-blocks")}</Tab>
							</TabList>
							<TabPanels>
								<TabPanel>
									<Reset
										saved={background}
										resetKey="background"
										onReset={(v) => {
											setAttributes({ background: v });
										}}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									/>
									<Background
										value={background}
										onChange={(v) =>
											setAttributes({ background: v })
										}
									/>
								</TabPanel>
							</TabPanels>
						</Tabs>
					</PopoverDrawer>
				</Panel>
				<Panel title={__("Icon", "magazine-blocks")}>
					<PopoverDrawer
						label={__("Background", "magazine-blocks")}
						trigger={(props) => {
							const Trigger = () => (
								<PopoverDrawer.Trigger {...props}>
									<ZStack
										items={[
											{
												color: shapeColor,
												id: "shapeColor",
											},
										]}
									/>
								</PopoverDrawer.Trigger>
							);
							if (isCSSVariable(shapeColor)) {
								return (
									<Box position="relative">
										<Trigger />
										<Global
											position="absolute"
											fill="primary.500"
											height="14px"
											width="14px"
											top="-5px"
											left="-5px"
											bg="white"
											borderRadius="full"
										/>
									</Box>
								);
							}
							return <Trigger />;
						}}
						closeOnFocusOutside
					>
						<Tabs position="relative">
							<TabList>
								<Tab>{__("Normal", "magazine-blocks")}</Tab>
							</TabList>
							<TabPanels>
								<TabPanel>
									<Reset
										saved={shapeColor}
										resetKey="shapeColor"
										onReset={(v) => {
											setAttributes({ shapeColor: v });
										}}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									/>
									<ColorPicker
										value={shapeColor}
										onChange={(v) =>
											setAttributes({ shapeColor: v })
										}
									/>
								</TabPanel>
							</TabPanels>
						</Tabs>
					</PopoverDrawer>
					<Slider
						label={__("Border Radius", "magazine-blocks")}
						value={shapeBorderRadius}
						min={0}
						max={100}
						step={1}
						onChange={(val) =>
							setAttributes({ shapeBorderRadius: val })
						}
						resetAttributeKey="shapeBorderRadius"
					/>
					<Dimensions
						value={shapePadding}
						responsive
						label={__("Padding", "magazine-blocks")}
						defaultUnit="px"
						units={["px", "rem", "em", "%"]}
						onChange={(val) => setAttributes({ shapePadding: val })}
						type="shapePadding"
						resetAttributeKey="shapePadding"
					/>
					<Slider
						label={__("Outline Width", "magazine-blocks")}
						value={shapeOutlineWidth}
						min={0}
						max={20}
						step={1}
						onChange={(val) =>
							setAttributes({ shapeOutlineWidth: val })
						}
						resetAttributeKey="shapeOutlineWidth"
					/>
					<PopoverDrawer
						label={__("Outline Color", "magazine-blocks")}
						trigger={(props) => (
							<PopoverDrawer.Trigger {...props}>
								<ZStack
									items={[
										{
											color: shapeOutlineColor,
											id: "shapeOutlineColor",
										},
									]}
								/>
							</PopoverDrawer.Trigger>
						)}
						closeOnFocusOutside
					>
						<Tabs position="relative">
							<TabList>
								<Tab>{__("Normal", "magazine-blocks")}</Tab>
							</TabList>
							<TabPanels>
								<TabPanel>
									<Reset
										saved={shapeOutlineColor}
										resetKey="shapeOutlineColor"
										onReset={(v) => {
											setAttributes({
												shapeOutlineColor: v,
											});
										}}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									/>
									<ColorPicker
										value={shapeOutlineColor}
										onChange={(v) =>
											setAttributes({
												shapeOutlineColor: v,
											})
										}
									/>
								</TabPanel>
							</TabPanels>
						</Tabs>
					</PopoverDrawer>
				</Panel>
				<Panel title={__("Border", "magazine-blocks")}>
					<Tabs variant="border">
						<TabList>
							<Tab>Normal</Tab>
							<Tab>Hover</Tab>
						</TabList>
						<TabPanels>
							<TabPanel>
								<Border
									value={border}
									onChange={(v) => {
										setAttributes({
											border: v,
										});
									}}
									resetAttributeKey="border"
								/>
								<BoxShadow
									resetAttributeKey="boxShadow"
									value={boxShadow}
									onChange={(val) =>
										setAttributes({
											boxShadow: val,
										})
									}
								/>
							</TabPanel>
							<TabPanel>
								<Border
									value={hoverBorder}
									onChange={(v) => {
										setAttributes({
											hoverBorder: v,
										});
									}}
									resetAttributeKey="hoverBorder"
								/>
								<BoxShadow
									resetAttributeKey="boxShadowHover"
									value={boxShadowHover}
									onChange={(val) =>
										setAttributes({
											boxShadowHover: val,
										})
									}
								/>
							</TabPanel>
						</TabPanels>
					</Tabs>
				</Panel>
			</StyleInspectorControls>
			<AdvancedInspectorControls>
				<AdvancedControls
					blockMargin={blockMargin}
					setAttributes={setAttributes}
					blockPadding={blockPadding}
					blockZIndex={blockZIndex}
					cssID={cssID}
					className={className}
					// hasblockHTML={false}
				/>
				<ResponsiveControls
					{...{
						hideOnDesktop,
						hideOnTablet,
						hideOnMobile,
						setAttributes,
					}}
				/>
			</AdvancedInspectorControls>
		</>
	);
};

export default InspectorControls;
